import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AlertColor } from '@mui/material';

import type { definitions } from '@luxuryescapes/contract-svc-promo';

import DateWidget from '~/components/Common/Elements/DateWidget';
import PageHeader from '~/components/Common/Elements/PageHeader';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import PromoAdminRequiredIndicator from '~/components/Promo/PromoAdminRequiredIndicator';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import usePromoAdminPermission from '~/components/Promo/hooks/usePromoPermission';
import VoucherConditions from '~/components/Vouchers/VoucherConditions';
import VoucherOrderAutoAssignByOrderFiltersTable from '~/components/Vouchers/VoucherOrderAutoAssignByOrderFiltersTable';
import VoucherUsersSearchPage from '~/components/Vouchers/VoucherUsersSearchPage';

import { useCreateVoucherAutoAssignmentRule } from '~/hooks/apis/promo/useCreateVoucherAutoAssignmentRule';
import { useDeleteVoucherAutoAssignmentRule } from '~/hooks/apis/promo/useDeleteVoucherAutoAssignmentRule';
import { useFindVoucherAutoAssignmentRule } from '~/hooks/apis/promo/useFindVoucherAutoAssignmentRule';
import { useListVoucherAutoAssignmentRuleAssignmentGroups } from '~/hooks/apis/promo/useListVoucherAutoAssignmentRuleAssignmentGroups';
import { useUpdateVoucherAutoAssignmentRule } from '~/hooks/apis/promo/useUpdateVoucherAutoAssignmentRule';
import {
  useDryRunVoucherOrderAutoAssign,
  useManualVoucherOrderAutoAssign,
} from '~/hooks/apis/promo/useVoucherOrderAutoAssign';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import {
  type CreateVoucherAutoAssignmentRulePayload,
  type GetVoucherOrderAutoAssignResponse,
  VOUCHER_USER_REPORTING_TAG_AUTO_ASSIGN_ORDER,
  type Voucher,
  getVoucherWarnings,
  listVouchers,
} from '~/services/PromoService';

import ApiError from '~/types/ApiError';

import { normalizeToISODate } from '~/utils/dateFormatter';

import VoucherSummarySection from './VoucherSummarySection';
import { voucherAutoAssignGroupPath } from './voucherAutoAssignRulePaths';

type PromoBrand = definitions['Promo Brands'];
type PromoRegion = definitions['Promo Regions'];
type VoucherCondition = definitions['VoucherConditionV1'];

/** Row from order dry-run `conditionOutcomes`; contract may lag behind API `metadata`. */
type VoucherOrderAutoAssignConditionOutcome =
  GetVoucherOrderAutoAssignResponse['result']['outcomes'][number]['conditionOutcomes'][number];

function getConditionOutcomeMetadata(outcome: VoucherOrderAutoAssignConditionOutcome): unknown {
  const extended = outcome as VoucherOrderAutoAssignConditionOutcome & { metadata?: unknown };
  return extended.metadata;
}

function formatConditionCheckMetadata(metadata: unknown): string | null {
  if (metadata === undefined || metadata === null) {
    return null;
  }
  if (typeof metadata === 'string') {
    return metadata;
  }
  try {
    return JSON.stringify(metadata, null, 2);
  } catch {
    return String(metadata);
  }
}

const FALLBACK_REGIONS: Array<PromoRegion> = ['AU', 'NZ', 'US', 'GB', 'SG', 'HK'];

const DEFAULT_VOUCHER_AUTO_ASSIGN_RULE_TYPE = 'order-complete';

const VOUCHER_AUTO_ASSIGN_RULE_NEW_PATH = '/marketing/voucher-auto-assign-rules/create';

/** Maps `request()` / `ApiError` failures into copy for alerts and snackbars. */
function formatRequestFailure(err: unknown): string {
  if (err instanceof ApiError) {
    const parts: Array<string> = [];
    const headline = err.message?.trim();
    if (headline) {
      parts.push(headline);
    }
    if (err.status != null) {
      parts.push(`HTTP ${err.status}`);
    }
    const { errors } = err;
    let bodyDetails: string | null = null;
    if (errors !== undefined && errors !== null) {
      if (typeof errors === 'string') {
        const trimmed = errors.trim();
        bodyDetails = trimmed.length > 0 ? trimmed : null;
      } else if (Array.isArray(errors)) {
        bodyDetails = errors.length > 0 ? JSON.stringify(errors, null, 2) : null;
      } else {
        const serialized = JSON.stringify(errors, null, 2);
        bodyDetails = serialized !== '{}' && serialized !== 'null' ? serialized : null;
      }
    }
    if (bodyDetails) {
      parts.push(bodyDetails);
    }
    return parts.length > 0 ? parts.join(' — ') : 'Request failed with no message from the server.';
  }
  if (err instanceof Error) {
    return err.message.trim() || 'Something went wrong.';
  }
  try {
    return JSON.stringify(err, null, 2);
  } catch {
    return String(err);
  }
}

function getVoucherWarningSeverity(level?: string): AlertColor {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'success') {
    return level;
  }
  return 'warning';
}

/**
 * Resolves voucher id only when a listVouchers row’s name equals the trimmed input exactly (no first-result fallback).
 */
function resolveVoucherIdFromListResults(
  trimmedInput: string,
  vouchers: Array<{ voucherId: string; voucherName: string }>,
) {
  const exactMatches = vouchers.filter((v) => (v.voucherName ?? '').trim() === trimmedInput);
  if (exactMatches.length === 1) {
    return exactMatches[0].voucherId;
  }
  if (exactMatches.length > 1) {
    throw new Error(
      `Multiple vouchers share the exact name "${trimmedInput}". Resolve the ambiguity in Voucher check or contact support.`,
    );
  }
  if (vouchers.length === 0) {
    throw new Error(
      `No vouchers matched "${trimmedInput}" for the current tenant brand. Check the name in Voucher check.`,
    );
  }
  throw new Error(
    `No exact name match for "${trimmedInput}". Search returned ${vouchers.length} similar result(s); enter the full voucher name exactly as in Voucher check, then Confirm.`,
  );
}

export default function VoucherAutoAssignRuleFormPage() {
  const { push } = useAppNavigation();
  const location = useLocation();
  const params = useParams<{ ruleId?: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const isPromoAdmin = usePromoAdminPermission();
  const { tenant } = useCurrentTenant();
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  const tenantBrand = useMemo<PromoBrand>(() => (tenant.brand as PromoBrand) || 'luxuryescapes', [tenant.brand]);
  const prevTenantBrandRef = useRef(tenantBrand);

  const isCreate = location.pathname.replace(/\/+$/, '') === VOUCHER_AUTO_ASSIGN_RULE_NEW_PATH;
  const ruleId = isCreate ? undefined : params.ruleId;
  const groupAssignmentKeyFromUrl = useMemo(
    () => new URLSearchParams(location.search).get('assignment_group')?.trim() ?? '',
    [location.search],
  );
  const sourceRuleIdFromUrl = useMemo(
    () => new URLSearchParams(location.search).get('source_rule_id')?.trim() ?? '',
    [location.search],
  );

  /** Avoid re-applying `existingRule` on every React Query refetch (clears in-progress edits). */
  const lastHydratedRuleIdRef = useRef<string | null>(null);
  const lastHydratedSourceRuleIdRef = useRef<string | null>(null);
  /**
   * True once this session has a voucher name tied to the rule (hydrated embedded voucher, lookup, or user search).
   * Used on save when the name field is empty: we must not fall back to the server voucher_id after the user cleared a bound name.
   */
  const hadVoucherNameBindingRef = useRef(false);

  const [ruleType, setRuleType] = useState(() => (isCreate ? DEFAULT_VOUCHER_AUTO_ASSIGN_RULE_TYPE : ''));

  const {
    data: ruleFetch,
    isLoading: isRuleLoading,
    isError: isRuleError,
    error: ruleError,
  } = useFindVoucherAutoAssignmentRule({ ruleId });
  const existingRule = ruleFetch?.rule;
  /** Optional embedded voucher on GET rule — may be undefined even when `rule.voucherId` is present. */
  const ruleVoucher = ruleFetch?.voucher;
  const {
    data: sourceRuleFetch,
    isLoading: isSourceRuleLoading,
    isError: isSourceRuleError,
    error: sourceRuleError,
  } = useFindVoucherAutoAssignmentRule({ ruleId: isCreate ? sourceRuleIdFromUrl : undefined });
  const sourceRule = sourceRuleFetch?.rule;
  const sourceRuleVoucher = sourceRuleFetch?.voucher;

  const createMutation = useCreateVoucherAutoAssignmentRule();
  const updateMutation = useUpdateVoucherAutoAssignmentRule();
  const deleteMutation = useDeleteVoucherAutoAssignmentRule();
  const dryRunMutation = useDryRunVoucherOrderAutoAssign();
  const manualApplyMutation = useManualVoucherOrderAutoAssign();

  const regionOptions = useMemo(() => {
    const groupings = promoMeta?.promo_region_groupings ?? [];
    const codes = new Set<string>();
    groupings.forEach((g) => {
      (g.regionCodes ?? []).forEach((c) => codes.add(c));
    });
    const sorted = Array.from(codes).sort() as Array<PromoRegion>;
    return sorted.length > 0 ? sorted : FALLBACK_REGIONS;
  }, [promoMeta?.promo_region_groupings]);

  const ruleTypeOptions = useMemo(() => {
    const fromOpts = promoMeta?.voucher_auto_assignment_rule_options?.rule_types;
    if (fromOpts?.length) {
      return fromOpts;
    }
    const fallback = promoMeta?.voucher_auto_assignment_order_complete_rule_types ?? [];
    return fallback;
  }, [promoMeta]);

  const assignmentBehaviorOptions = useMemo(() => {
    const fromOpts = promoMeta?.voucher_auto_assignment_rule_options?.assignment_behaviors;
    if (fromOpts?.length) {
      return fromOpts;
    }
    return promoMeta?.voucher_auto_assignment_assignment_behavior_options ?? [];
  }, [promoMeta]);

  const allowedConditionTypeKeys = useMemo(
    () => promoMeta?.voucher_auto_assignment_rule_options?.rule_condition_types_supported?.map((c) => c.key) ?? [],
    [promoMeta],
  );
  const { data: assignmentGroupsData, isLoading: isAssignmentGroupsLoading } =
    useListVoucherAutoAssignmentRuleAssignmentGroups();
  const assignmentGroupOptions = useMemo(
    () => assignmentGroupsData?.result.assignmentGroups.map((group) => group.assignmentGroup) ?? [],
    [assignmentGroupsData?.result.assignmentGroups],
  );

  /** Voucher id sent to the API — set from listVouchers after name blur / Confirm (or hydrated on edit). */
  const [resolvedVoucherId, setResolvedVoucherId] = useState('');
  /** Trimmed voucher name that `resolvedVoucherId` was last matched to; null if id is only from server with no name. */
  const [voucherIdSourceName, setVoucherIdSourceName] = useState<string | null>(null);
  const [voucherNameInput, setVoucherNameInput] = useState('');
  const [isResolvingVoucher, setIsResolvingVoucher] = useState(false);
  const [regionCodes, setRegionCodes] = useState<Array<PromoRegion>>([]);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [assignmentGroup, setAssignmentGroup] = useState('');
  const [applyOrder, setApplyOrder] = useState('');
  const [assignmentBehavior, setAssignmentBehavior] = useState<string>('');
  const [conditions, setConditions] = useState<Array<VoucherCondition>>([]);
  const [orderIdForTest, setOrderIdForTest] = useState('');
  const [testedOrderId, setTestedOrderId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<GetVoucherOrderAutoAssignResponse | null>(null);
  const [bulkApplyOrderId, setBulkApplyOrderId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const selectRegionMenuCodes = useMemo(() => {
    const merged = new Set<PromoRegion>(regionOptions);
    regionCodes.forEach((c) => merged.add(c));
    return Array.from(merged).sort();
  }, [regionOptions, regionCodes]);

  useEffect(() => {
    setTestedOrderId(null);
    setTestResult(null);
  }, [orderIdForTest]);

  useEffect(() => {
    if (isCreate) {
      lastHydratedRuleIdRef.current = null;
      return;
    }
    if (!existingRule) {
      return;
    }
    const rid = existingRule.voucherAutoAssignmentRuleId;
    if (lastHydratedRuleIdRef.current === rid) {
      return;
    }
    lastHydratedRuleIdRef.current = rid;

    const hydratedName = ruleVoucher?.voucherName?.trim() ?? '';
    hadVoucherNameBindingRef.current = Boolean(hydratedName);
    setResolvedVoucherId(existingRule.voucherId);
    setVoucherNameInput(hydratedName);
    setVoucherIdSourceName(hydratedName || null);
    setRuleType(existingRule.ruleType);
    setRegionCodes((existingRule.regions ?? []) as Array<PromoRegion>);
    const tz = window.configs.DEFAULT_TIMEZONE;
    const startParsed = existingRule.startAssignmentDate ? dayjs(existingRule.startAssignmentDate) : null;
    const endParsed = existingRule.endAssignmentDate ? dayjs(existingRule.endAssignmentDate) : null;
    setStartAt(startParsed?.isValid() ? startParsed.tz(tz).format('YYYY-MM-DD') : '');
    setEndAt(endParsed?.isValid() ? endParsed.tz(tz).format('YYYY-MM-DD') : '');
    setIsActive(existingRule.isActive);
    setAssignmentGroup(existingRule.assignmentGroup ?? '');
    setApplyOrder(existingRule.applyOrder != null ? String(existingRule.applyOrder) : '');
    const rd = existingRule.ruleData;
    const behavior = rd.assignmentBehavior ?? '';
    setAssignmentBehavior(typeof behavior === 'string' ? behavior : '');
    const rawConditions = rd.rule_conditions ?? [];
    setConditions(rawConditions);
  }, [ruleFetch, isCreate, existingRule, ruleVoucher?.voucherName]);

  useEffect(() => {
    if (prevTenantBrandRef.current !== tenantBrand) {
      prevTenantBrandRef.current = tenantBrand;
      hadVoucherNameBindingRef.current = false;
      setResolvedVoucherId('');
      setVoucherIdSourceName(null);
    }
  }, [tenantBrand]);

  /** When optional `voucher` appears on the rule response after the first load, sync name + source binding. */
  useEffect(() => {
    if (isCreate || !existingRule) {
      return;
    }
    const name = ruleVoucher?.voucherName?.trim();
    if (name) {
      hadVoucherNameBindingRef.current = true;
      setVoucherNameInput(name);
      setVoucherIdSourceName(name);
    }
  }, [isCreate, existingRule, ruleVoucher?.voucherName]);

  useEffect(() => {
    if (!isCreate) {
      return;
    }
    if (sourceRuleIdFromUrl && !sourceRule) {
      return;
    }
    hadVoucherNameBindingRef.current = false;
    setResolvedVoucherId('');
    setVoucherIdSourceName(null);
    setVoucherNameInput('');
    setRegionCodes([]);
    setAssignmentGroup(groupAssignmentKeyFromUrl);
    setApplyOrder('');
  }, [groupAssignmentKeyFromUrl, isCreate, sourceRule, sourceRuleIdFromUrl]);

  useEffect(() => {
    if (!isCreate || !sourceRule || lastHydratedSourceRuleIdRef.current === sourceRule.voucherAutoAssignmentRuleId) {
      return;
    }
    lastHydratedSourceRuleIdRef.current = sourceRule.voucherAutoAssignmentRuleId;

    const hydratedName = sourceRuleVoucher?.voucherName?.trim() ?? '';
    hadVoucherNameBindingRef.current = Boolean(hydratedName);
    setResolvedVoucherId(sourceRule.voucherId);
    setVoucherNameInput(hydratedName);
    setVoucherIdSourceName(hydratedName || null);
    setRuleType(sourceRule.ruleType || DEFAULT_VOUCHER_AUTO_ASSIGN_RULE_TYPE);
    setRegionCodes((sourceRule.regions ?? []) as Array<PromoRegion>);
    const tz = window.configs.DEFAULT_TIMEZONE;
    const startParsed = sourceRule.startAssignmentDate ? dayjs(sourceRule.startAssignmentDate) : null;
    const endParsed = sourceRule.endAssignmentDate ? dayjs(sourceRule.endAssignmentDate) : null;
    setStartAt(startParsed?.isValid() ? startParsed.tz(tz).format('YYYY-MM-DD') : '');
    setEndAt(endParsed?.isValid() ? endParsed.tz(tz).format('YYYY-MM-DD') : '');
    setIsActive(sourceRule.isActive);
    setAssignmentGroup(groupAssignmentKeyFromUrl || sourceRule.assignmentGroup || '');
    setApplyOrder(sourceRule.applyOrder != null ? String(sourceRule.applyOrder) : '');
    const behavior = sourceRule.ruleData.assignmentBehavior ?? '';
    setAssignmentBehavior(typeof behavior === 'string' ? behavior : '');
    setConditions(sourceRule.ruleData.rule_conditions ?? []);
  }, [groupAssignmentKeyFromUrl, isCreate, sourceRule, sourceRuleVoucher?.voucherName]);

  const runVoucherLookup = useCallback(async (): Promise<string | null> => {
    const raw = voucherNameInput.trim();
    if (!raw) {
      enqueueSnackbar(
        'Enter a voucher name (as in Voucher check), then use Confirm, leave the field, or save to look up the voucher ID.',
        { variant: 'warning' },
      );
      return null;
    }
    const source = voucherIdSourceName?.trim() ?? '';
    if (raw === source && resolvedVoucherId.trim()) {
      return resolvedVoucherId.trim();
    }
    setIsResolvingVoucher(true);
    try {
      const listRes = await listVouchers({ voucher_name: raw, brand: tenantBrand, limit: 25 });
      const vouchers = listRes.result?.vouchers ?? [];
      const id = resolveVoucherIdFromListResults(raw, vouchers);
      hadVoucherNameBindingRef.current = true;
      setResolvedVoucherId(id);
      setVoucherIdSourceName(raw);
      return id;
    } catch (err) {
      enqueueSnackbar(`Voucher search failed: ${formatRequestFailure(err)}`, { variant: 'error' });
      setResolvedVoucherId('');
      setVoucherIdSourceName(null);
      return null;
    } finally {
      setIsResolvingVoucher(false);
    }
  }, [voucherNameInput, voucherIdSourceName, resolvedVoucherId, tenantBrand, enqueueSnackbar]);

  const handleVoucherNameChange = (next: string) => {
    setVoucherNameInput(next);
    const t = next.trim();
    if (!resolvedVoucherId) {
      return;
    }
    if (voucherIdSourceName === null) {
      if (t.length > 0) {
        setResolvedVoucherId('');
      }
      return;
    }
    if (t !== voucherIdSourceName) {
      setResolvedVoucherId('');
      setVoucherIdSourceName(null);
    }
  };

  const handleVoucherNameBlur = () => {
    const t = voucherNameInput.trim();
    if (!t) {
      if (isCreate) {
        setResolvedVoucherId('');
        setVoucherIdSourceName(null);
      } else if (voucherIdSourceName !== null) {
        setResolvedVoucherId('');
        setVoucherIdSourceName(null);
      }
      return;
    }
    if (t === voucherIdSourceName && resolvedVoucherId) {
      return;
    }
    void runVoucherLookup();
  };

  const handleConfirmVoucherName = useCallback(async () => {
    const id = await runVoucherLookup();
    if (id) {
      enqueueSnackbar('Voucher name found and ID confirmed for this brand.', { variant: 'success' });
    }
  }, [runVoucherLookup, enqueueSnackbar]);

  const ruleOutcomeForOrder = useMemo(() => {
    if (!testResult || !ruleId) {
      return undefined;
    }
    return testResult.result.outcomes.find((o) => o.voucherAutoAssignmentRuleId === ruleId);
  }, [testResult, ruleId]);

  const canApplyRuleToTestedOrder = Boolean(
    ruleId && testedOrderId && orderIdForTest.trim() === testedOrderId && testResult && ruleOutcomeForOrder?.matched,
  );

  const handleTestRuleOnOrder = async () => {
    const oid = orderIdForTest.trim();
    if (!oid) {
      enqueueSnackbar('Enter a purchase order UUID to run the dry-run.', { variant: 'warning' });
      return;
    }
    if (!ruleId) {
      enqueueSnackbar('Cannot test: rule ID is missing. Save the rule or open it from the list.', {
        variant: 'warning',
      });
      return;
    }
    try {
      const res = await dryRunMutation.mutateAsync(oid);
      setTestedOrderId(oid);
      setTestResult(res);
      const outcome = res.result.outcomes.find((o) => o.voucherAutoAssignmentRuleId === ruleId);
      if (!outcome) {
        enqueueSnackbar(
          'Dry-run finished, but the response had no outcome row for this rule (check other rules in the payload or confirm the order ID).',
          { variant: 'warning' },
        );
      } else if (outcome.matched) {
        enqueueSnackbar('Dry-run: this rule matched the order. You can use “Apply to order” below.', {
          variant: 'success',
        });
      } else {
        enqueueSnackbar('Dry-run: this rule did not match the order. See the details on the page.', {
          variant: 'info',
        });
      }
    } catch (err) {
      enqueueSnackbar(`Dry-run failed: ${formatRequestFailure(err)}`, { variant: 'error' });
    }
  };

  const handleManualApplyToOrder = async () => {
    const oid = orderIdForTest.trim();
    if (!isPromoAdmin) {
      enqueueSnackbar('Requires promo-admin permission to assign voucher auto-assign rules.', { variant: 'warning' });
      return;
    }
    if (!canApplyRuleToTestedOrder) {
      return;
    }
    try {
      await manualApplyMutation.mutateAsync({ orderId: oid });
      enqueueSnackbar('Manual voucher auto-assign completed for this order.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Manual apply failed: ${formatRequestFailure(err)}`, { variant: 'error' });
    }
  };

  const handleManualApplyFromSearch = useCallback(
    async (orderId: string) => {
      if (!isPromoAdmin) {
        enqueueSnackbar('Requires promo-admin permission to assign voucher auto-assign rules.', { variant: 'warning' });
        return;
      }
      const oid = orderId.trim();
      if (!oid) {
        return;
      }
      setBulkApplyOrderId(oid);
      try {
        await manualApplyMutation.mutateAsync({ orderId: oid });
        enqueueSnackbar(`Manual voucher auto-assign completed for order ${oid}.`, { variant: 'success' });
      } catch (err) {
        enqueueSnackbar(`Manual apply failed for order ${oid}: ${formatRequestFailure(err)}`, {
          variant: 'error',
        });
      } finally {
        setBulkApplyOrderId(null);
      }
    },
    [enqueueSnackbar, isPromoAdmin, manualApplyMutation],
  );

  const buildPayload = (
    resolvedVoucherId: string,
    assignmentLocals?: { startLocal: string; endLocal: string },
  ): CreateVoucherAutoAssignmentRulePayload => {
    const startLocal = assignmentLocals?.startLocal ?? startAt;
    const endLocal = assignmentLocals?.endLocal ?? endAt;
    const tz = window.configs.DEFAULT_TIMEZONE;
    const startIso = dayjs.tz(startLocal, 'YYYY-MM-DD', tz).startOf('day').toISOString();
    const endIso = dayjs.tz(endLocal, 'YYYY-MM-DD', tz).endOf('day').toISOString();
    const rule_data: CreateVoucherAutoAssignmentRulePayload['rule_data'] = {
      rule_conditions: conditions,
    };
    if (assignmentBehavior === 'single-assignment-only' || assignmentBehavior === 'allow-multiple') {
      rule_data.assignmentBehavior = assignmentBehavior;
    }
    const payload: CreateVoucherAutoAssignmentRulePayload = {
      voucher_id: resolvedVoucherId.trim(),
      rule_type: ruleType.trim(),
      rule_data,
      start_assignment_date: normalizeToISODate(startIso),
      end_assignment_date: normalizeToISODate(endIso),
      is_active: isActive,
      regions: [...new Set(regionCodes)],
      brand: tenantBrand,
    };
    const trimmedAssignmentGroup = assignmentGroup.trim();
    const trimmedApplyOrder = applyOrder.trim();
    if (trimmedAssignmentGroup && trimmedApplyOrder) {
      payload.assignment_group = trimmedAssignmentGroup;
      payload.apply_order = Number(trimmedApplyOrder);
    } else if (!isCreate) {
      payload.assignment_group = null;
      payload.apply_order = null;
    }
    return payload;
  };

  const handleSubmit = async () => {
    if (!isPromoAdmin) {
      enqueueSnackbar('Requires promo-admin permission to edit voucher auto-assign rules.', { variant: 'warning' });
      return;
    }
    const trimmedName = voucherNameInput.trim();
    let voucherIdToSave: string;

    if (trimmedName) {
      const id = await runVoucherLookup();
      if (!id) {
        return;
      }
      voucherIdToSave = id;
    } else if (isCreate) {
      enqueueSnackbar(
        'Enter a voucher name (as in Voucher check), then Confirm, leave the field, or save to resolve the voucher ID.',
        {
          variant: 'warning',
        },
      );
      return;
    } else {
      const fromState = resolvedVoucherId.trim();
      const fromServer = existingRule?.voucherId?.trim() ?? '';
      const id = fromState || fromServer;
      if (!id) {
        enqueueSnackbar('Voucher could not be determined. Enter a voucher name to search.', { variant: 'warning' });
        return;
      }
      if (hadVoucherNameBindingRef.current && !fromState) {
        enqueueSnackbar(
          'Enter a voucher name (as in Voucher check) and Confirm or leave the field to resolve the ID before saving.',
          { variant: 'warning' },
        );
        return;
      }
      voucherIdToSave = id;
    }
    if (!ruleType.trim()) {
      enqueueSnackbar('Choose or enter a rule type before saving.', { variant: 'warning' });
      return;
    }
    if (regionCodes.length === 0) {
      enqueueSnackbar('Select at least one region where this rule applies.', { variant: 'warning' });
      return;
    }
    const trimmedAssignmentGroup = assignmentGroup.trim();
    const trimmedApplyOrder = applyOrder.trim();
    if ((trimmedAssignmentGroup && !trimmedApplyOrder) || (!trimmedAssignmentGroup && trimmedApplyOrder)) {
      enqueueSnackbar('Assignment group and apply order must be provided together.', { variant: 'warning' });
      return;
    }
    if (trimmedApplyOrder) {
      const parsedApplyOrder = Number(trimmedApplyOrder);
      if (!Number.isInteger(parsedApplyOrder)) {
        enqueueSnackbar('Apply order must be a whole number.', { variant: 'warning' });
        return;
      }
    }
    const startRaw = startAt.trim();
    if (!startRaw) {
      enqueueSnackbar('Set a start date in “Start assignment”.', { variant: 'warning' });
      return;
    }
    if (!dayjs(startRaw, 'YYYY-MM-DD', true).isValid()) {
      enqueueSnackbar('Start assignment is not a valid date. Choose the start again.', {
        variant: 'warning',
      });
      return;
    }
    const endRaw = endAt.trim();
    if (!endRaw) {
      enqueueSnackbar('Set an end date in “End assignment”.', { variant: 'warning' });
      return;
    }
    if (!dayjs(endRaw, 'YYYY-MM-DD', true).isValid()) {
      enqueueSnackbar('End assignment is not a valid date. Choose the end again.', {
        variant: 'warning',
      });
      return;
    }
    if (endRaw < startRaw) {
      enqueueSnackbar('End assignment must be on or after start assignment.', { variant: 'warning' });
      return;
    }

    const saveVerb = isCreate ? 'create' : 'save';
    const assignmentLocals = { startLocal: startRaw, endLocal: endRaw };
    try {
      if (isCreate) {
        await createMutation.mutateAsync(buildPayload(voucherIdToSave, assignmentLocals));
        enqueueSnackbar('Voucher auto-assign rule created.', { variant: 'success' });
      } else if (ruleId) {
        await updateMutation.mutateAsync({
          voucherAutoAssignmentRuleId: ruleId,
          payload: buildPayload(voucherIdToSave, assignmentLocals),
        });
        enqueueSnackbar('Voucher auto-assign rule updated.', { variant: 'success' });
      } else {
        enqueueSnackbar('Cannot save: rule ID is missing. Open the rule from the list and try again.', {
          variant: 'error',
        });
        return;
      }
      const returnAssignmentGroup = trimmedAssignmentGroup || groupAssignmentKeyFromUrl;
      push(
        returnAssignmentGroup
          ? voucherAutoAssignGroupPath(returnAssignmentGroup)
          : '/marketing/voucher-auto-assign-rules',
      );
    } catch (err) {
      enqueueSnackbar(`Could not ${saveVerb} voucher auto-assign rule: ${formatRequestFailure(err)}`, {
        variant: 'error',
      });
    }
  };

  const handleConfirmDeleteRule = async () => {
    if (!isPromoAdmin) {
      enqueueSnackbar('Requires promo-admin permission to edit voucher auto-assign rules.', { variant: 'warning' });
      return;
    }
    if (!ruleId) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(ruleId);
      setDeleteConfirmOpen(false);
      enqueueSnackbar('Voucher auto-assign rule deleted.', { variant: 'success' });
      push('/marketing/voucher-auto-assign-rules');
    } catch (err) {
      enqueueSnackbar(`Could not delete voucher auto-assign rule: ${formatRequestFailure(err)}`, {
        variant: 'error',
      });
    }
  };

  const pageTitle = isCreate ? 'Create voucher auto-assign rule' : 'Edit voucher auto-assign rule';
  const saving = createMutation.isPending || updateMutation.isPending || isResolvingVoucher || deleteMutation.isPending;
  const showRuleSpinner = !isCreate && Boolean(ruleId) && isRuleLoading;
  const showSourceRuleSpinner = isCreate && Boolean(sourceRuleIdFromUrl) && isSourceRuleLoading;
  const showNotFound = !isCreate && Boolean(ruleId) && !isRuleLoading && ruleFetch === null && !isRuleError;

  const voucherNameFieldHelperText = useMemo(() => {
    if (isCreate) {
      return 'Same name as Voucher check. Use Confirm, leave the field, or save to look up the voucher and resolve the ID.';
    }
    if (ruleVoucher) {
      return 'Search for the voucher name to assign to this rule. Click Confirm to resolve the voucher ID.';
    }
    return 'The rule response may omit voucher. Enter the Voucher check name, then Confirm or blur—or use Save—to resolve the stored voucher ID.';
  }, [isCreate, ruleVoucher]);

  const voucherIdStoredHelperText = useMemo(() => {
    if (isCreate) {
      return 'Filled after a successful name lookup. Save resolves the ID from the voucher name when a name is entered.';
    }
    return 'The voucher_id saved on this rule. Change the voucher name and Confirm or blur the field, or save — the ID is resolved from the name via search when a name is present.';
  }, [isCreate]);

  const linkedVoucherWarnings = useMemo(() => {
    if (!ruleVoucher) {
      return [];
    }
    return getVoucherWarnings(ruleVoucher as Voucher);
  }, [ruleVoucher]);

  const debugPageState = useMemo(
    () => ({
      route: { isCreate, ruleId, pathname: location.pathname },
      tenant: { tenantBrand },
      promoMeta: { isPromoMetaLoading, promoMeta },
      ruleQuery: {
        isRuleLoading,
        isRuleError,
        ruleError: isRuleError ? formatRequestFailure(ruleError) : null,
        ruleFetch,
        existingRule,
        ruleVoucher,
      },
      form: {
        ruleType,
        resolvedVoucherId,
        voucherIdSourceName,
        voucherNameInput,
        isResolvingVoucher,
        regionCodes,
        startAt,
        endAt,
        isActive,
        assignmentGroup,
        applyOrder,
        assignmentBehavior,
        conditions,
        showInfo,
        deleteConfirmOpen,
      },
      orderTest: {
        orderIdForTest,
        testedOrderId,
        testResult,
        ruleOutcomeForOrder,
        dryRunPending: dryRunMutation.isPending,
        manualApplyPending: manualApplyMutation.isPending,
      },
      mutations: {
        createPending: createMutation.isPending,
        updatePending: updateMutation.isPending,
        deletePending: deleteMutation.isPending,
      },
    }),
    [
      isCreate,
      ruleId,
      location.pathname,
      tenantBrand,
      isPromoMetaLoading,
      promoMeta,
      isRuleLoading,
      isRuleError,
      ruleError,
      ruleFetch,
      existingRule,
      ruleVoucher,
      ruleType,
      resolvedVoucherId,
      voucherIdSourceName,
      voucherNameInput,
      isResolvingVoucher,
      regionCodes,
      startAt,
      endAt,
      isActive,
      assignmentGroup,
      applyOrder,
      assignmentBehavior,
      conditions,
      showInfo,
      deleteConfirmOpen,
      orderIdForTest,
      testedOrderId,
      testResult,
      ruleOutcomeForOrder,
      dryRunMutation.isPending,
      manualApplyMutation.isPending,
      createMutation.isPending,
      updateMutation.isPending,
      deleteMutation.isPending,
    ],
  );

  return (
    <>
      <Helmet>
        <title>Marketing / {pageTitle}</title>
      </Helmet>
      <Container sx={{ mt: 2, mb: 4 }}>
        <PageHeader title={pageTitle}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PromoAdminRequiredIndicator action="Managing voucher auto-assignment rules" />
            <Button variant="text" color="primary" onClick={() => push('/marketing/voucher-auto-assign-rules')}>
              Back to list
            </Button>
            {assignmentGroup.trim() ? (
              <Button
                variant="text"
                color="primary"
                onClick={() => push(voucherAutoAssignGroupPath(assignmentGroup.trim()))}
              >
                View group
              </Button>
            ) : null}
            <Tooltip title={showInfo ? 'Hide field descriptions' : 'Show field descriptions'}>
              <IconButton
                color={showInfo ? 'primary' : 'default'}
                aria-pressed={showInfo}
                aria-label={showInfo ? 'Hide field descriptions' : 'Show field descriptions'}
                onClick={() => setShowInfo((v) => !v)}
                size="small"
                sx={{ border: 1, borderColor: 'divider' }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </PageHeader>
        <Box>
          <PageSubheader title="Targeting" />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These rules are used to auto-assign vouchers to orders based on specific conditions. If all conditions are
            met, users will be assigned the voucher on order complete
          </Typography>

          {!isCreate && assignmentGroup.trim() ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              This rule belongs to assignment group{' '}
              <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                {assignmentGroup.trim()}
              </Typography>
              . Consider the{' '}
              <Link component={RouterLink} to={voucherAutoAssignGroupPath(assignmentGroup.trim())}>
                full voucher auto-assignment group
              </Link>{' '}
              when making changes.
            </Alert>
          ) : null}

          {(isPromoMetaLoading || showRuleSpinner || showSourceRuleSpinner) && <Spinner />}

          {!isCreate && isRuleError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Could not load this voucher auto-assign rule. {formatRequestFailure(ruleError)}
            </Alert>
          )}

          {isCreate && isSourceRuleError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              Could not load the source voucher auto-assign rule. {formatRequestFailure(sourceRuleError)}
            </Alert>
          ) : null}

          {showNotFound && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Rule not found. It may have been deleted, or the ID in the URL is wrong. Return to the list and open the
              rule from there.
            </Alert>
          )}

          {!isPromoMetaLoading && !showRuleSpinner && !showSourceRuleSpinner && !showNotFound && (
            <Stack spacing={3} sx={{ mt: 2 }}>
              {!ruleTypeOptions.length && (
                <Alert severity="info">
                  Promo meta did not return voucher auto-assign rule types yet. You can still type a rule type key if
                  the API accepts it.
                </Alert>
              )}

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
                <Stack alignSelf="center">
                  <FormControlLabel
                    control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                    label="Rule active"
                  />
                  {showInfo ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', maxWidth: 280, pl: 4 }}
                    >
                      Inactive rules are ignored when orders are evaluated for auto-assignment.
                    </Typography>
                  ) : null}
                </Stack>
                {ruleTypeOptions.length > 0 ? (
                  <FormControl sx={{ minWidth: 280 }} required>
                    <InputLabel id="vaar-rule-type">Rule type</InputLabel>
                    <Select
                      labelId="vaar-rule-type"
                      label="Rule type"
                      value={ruleType}
                      onChange={(e) => setRuleType(e.target.value)}
                    >
                      <MenuItem value="">
                        <Typography component="span" sx={{ fontStyle: 'italic' }}>
                          Select rule type
                        </Typography>
                      </MenuItem>
                      {ruleType !== '' && !ruleTypeOptions.some((opt) => opt.key === ruleType) && (
                        <MenuItem value={ruleType}>{`${ruleType} (current)`}</MenuItem>
                      )}
                      {ruleTypeOptions.map((opt) => (
                        <MenuItem key={opt.key} value={opt.key}>
                          {opt.name} ({opt.key})
                        </MenuItem>
                      ))}
                    </Select>
                    {showInfo ? (
                      <FormHelperText>
                        Defines which lifecycle event evaluates this rule (for example when an order completes).
                      </FormHelperText>
                    ) : null}
                  </FormControl>
                ) : (
                  <TextField
                    required
                    label="Rule type"
                    value={ruleType}
                    onChange={(e) => setRuleType(e.target.value)}
                    sx={{ minWidth: 280 }}
                    helperText={
                      showInfo
                        ? 'Enter the rule type key accepted by the API for this trigger (see promo contract).'
                        : undefined
                    }
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
                <Autocomplete
                  freeSolo
                  options={assignmentGroupOptions}
                  value={assignmentGroup}
                  inputValue={assignmentGroup}
                  loading={isAssignmentGroupsLoading}
                  onChange={(_, nextValue) => setAssignmentGroup(nextValue ?? '')}
                  onInputChange={(_, nextInputValue) => setAssignmentGroup(nextInputValue)}
                  sx={{ minWidth: 280, flex: '1 1 280px', maxWidth: 480 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assignment group"
                      helperText={
                        showInfo
                          ? 'Choose an existing group key or enter a new one. All voucher rules in the assignment group will be run and only the best matching (by applyOrder) voucher will be assigned.'
                          : undefined
                      }
                    />
                  )}
                  loadingText="Loading assignment groups..."
                  noOptionsText="No assignment groups found"
                  clearOnBlur={false}
                  selectOnFocus
                  handleHomeEndKeys
                  slotProps={{
                    popper: {
                      sx: {
                        '& .MuiAutocomplete-option': {
                          fontFamily: 'monospace',
                          fontSize: 13,
                        },
                      },
                    },
                  }}
                />
                <TextField
                  label="Apply order"
                  type="number"
                  value={applyOrder}
                  onChange={(e) => setApplyOrder(e.target.value)}
                  sx={{ minWidth: 180, flex: '0 1 220px' }}
                  inputProps={{ step: 1 }}
                  helperText={
                    showInfo
                      ? 'Required when assignment group is set. The highest matching apply order in the group is assigned.'
                      : undefined
                  }
                />
              </Stack>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
                <FormControl sx={{ minWidth: 320, maxWidth: '100%' }} required>
                  <InputLabel id="vaar-region">Regions</InputLabel>
                  <Select
                    labelId="vaar-region"
                    id="vaar-region-select"
                    multiple
                    value={regionCodes}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRegionCodes(
                        typeof value === 'string'
                          ? (value.split(',') as Array<PromoRegion>)
                          : (value as Array<PromoRegion>),
                      );
                    }}
                    input={<OutlinedInput label="Regions" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as Array<PromoRegion>).map((code) => (
                          <Chip key={code} label={code} size="small" />
                        ))}
                      </Box>
                    )}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                  >
                    {selectRegionMenuCodes.map((code) => (
                      <MenuItem key={code} value={code}>
                        <Stack direction="row" spacing={1} alignItems="center" component="span">
                          <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {code}
                          </Typography>
                          {!regionOptions.includes(code) ? (
                            <Typography component="span" variant="caption" color="text.secondary">
                              (saved on rule)
                            </Typography>
                          ) : null}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  {showInfo ? (
                    <FormHelperText>
                      {
                        "Limit the rule to one or more promo region codes; the order's region must match one of the selected regions."
                      }
                    </FormHelperText>
                  ) : null}
                </FormControl>
                <Box sx={{ minWidth: 200, alignSelf: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand
                  </Typography>
                  <Typography variant="body2">{tenantBrand}</Typography>
                  {showInfo ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      The rule is only applied to this brand
                    </Typography>
                  ) : null}
                </Box>
              </Stack>

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
                <Box sx={{ minWidth: 280, flex: '1 1 280px', maxWidth: 400 }}>
                  <DateWidget
                    label="Start assignment"
                    value={startAt}
                    onChange={setStartAt}
                    slotProps={{
                      textField: {
                        required: true,
                        helperText: showInfo
                          ? 'First calendar day (tenant timezone) this rule is eligible to assign the voucher.'
                          : undefined,
                      },
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: 280, flex: '1 1 280px', maxWidth: 400 }}>
                  <DateWidget
                    label="End assignment"
                    value={endAt}
                    onChange={setEndAt}
                    slotProps={{
                      textField: {
                        required: true,
                        helperText: showInfo
                          ? 'Last calendar day (tenant timezone) this rule is eligible; end of that day in timezone.'
                          : undefined,
                      },
                    }}
                  />
                </Box>
              </Stack>

              <Box>
                <PageSubheader title="Rule conditions" />
                {showInfo ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    If an order meets all of the conditions below, the voucher will be auto-assigned to the user.
                  </Typography>
                ) : null}
                <VoucherConditions
                  conditions={conditions}
                  rewards={[]}
                  onChange={setConditions}
                  allowedConditionTypeKeys={allowedConditionTypeKeys.length ? allowedConditionTypeKeys : undefined}
                  additionalVoucherConditionTypeConfigs={
                    promoMeta?.voucher_auto_assignment_rule_options?.rule_condition_types_supported
                  }
                  includeRewardIdFilters={false}
                  showAdvanceDescription={showInfo}
                />
              </Box>
              <Box>
                <PageSubheader title="Reward" />

                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 0.25, sm: 2 }}
                    alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: { sm: 140 }, pt: { sm: 1 } }}
                    >
                      Assignment behavior
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={assignmentBehavior}
                      onChange={(e) => setAssignmentBehavior(e.target.value)}
                      helperText={
                        showInfo
                          ? 'Controls whether a customer may receive this voucher only once (subsequent assignments opportunities are ignored) or multiple times (we will attempt to assign the voucher to the user again). By default, after the voucher assignment has expired, unused we will allow re-assignment.'
                          : undefined
                      }
                      SelectProps={{
                        displayEmpty: true,
                        inputProps: { 'aria-label': 'Assignment behavior' },
                      }}
                      sx={{ flex: 1, minWidth: 0, maxWidth: 960 }}
                    >
                      <MenuItem value="">
                        <Typography component="span" sx={{ fontStyle: 'italic' }}>
                          Default (single-assignment-only)
                        </Typography>
                      </MenuItem>
                      {assignmentBehaviorOptions.map((opt) => (
                        <MenuItem key={opt.key} value={opt.key}>
                          {opt.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 0.25, sm: 2 }}
                    alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: { sm: 140 }, pt: { sm: 1 } }}
                    >
                      Voucher name
                      {isCreate ? ' *' : ''}
                    </Typography>
                    <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0, maxWidth: 960 }}>
                      <TextField
                        required={isCreate}
                        value={voucherNameInput}
                        onChange={(e) => handleVoucherNameChange(e.target.value)}
                        onBlur={handleVoucherNameBlur}
                        fullWidth
                        disabled={isResolvingVoucher}
                        helperText={showInfo ? voucherNameFieldHelperText : undefined}
                        inputProps={{ 'aria-label': 'Voucher name' }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ maxWidth: 'none', ml: 0.5 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                                {isResolvingVoucher ? <CircularProgress color="inherit" size={20} /> : null}
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  disabled={isResolvingVoucher || !voucherNameInput.trim()}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => void handleConfirmVoucherName()}
                                >
                                  Confirm
                                </Button>
                              </Stack>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                  </Stack>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 0.25, sm: 2 }}
                    alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: { sm: 140 }, pt: { sm: 1 } }}
                    >
                      Voucher ID (stored)
                    </Typography>
                    <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0, maxWidth: 960 }}>
                      <TextField
                        value={resolvedVoucherId}
                        fullWidth
                        disabled
                        helperText={showInfo ? voucherIdStoredHelperText : undefined}
                        inputProps={{ 'aria-label': 'Voucher ID (stored)' }}
                        sx={{ '& input': { fontFamily: 'monospace' } }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Box>

              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={saving || !isPromoAdmin}>
                  {isCreate ? 'Create' : 'Save'}
                </Button>
                {!isPromoAdmin ? (
                  <Tooltip title="Requires promo-admin permission to edit voucher auto-assign rules">
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                ) : null}
                <Box sx={{ flexGrow: 1, minWidth: 8 }} />
                {!isCreate && ruleId && existingRule ? (
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={saving || !isPromoAdmin}
                  >
                    Delete rule
                  </Button>
                ) : null}
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => push('/marketing/voucher-auto-assign-rules')}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </Stack>

              {!isCreate && existingRule ? (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <PageSubheader title="Linked voucher" />
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>
                    {showInfo ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Read-only summary of the voucher this rule assigns, when the API returns embedded voucher
                        details.
                      </Typography>
                    ) : null}
                    {linkedVoucherWarnings.length > 0 ? (
                      <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                        {linkedVoucherWarnings.map((warning, index) => (
                          <Alert
                            key={`${warning.code ?? 'voucher-warning'}-${index}`}
                            severity={getVoucherWarningSeverity(warning.level)}
                            variant="outlined"
                            sx={{
                              py: 0.25,
                              '& .MuiAlert-message': { py: 0.5, width: '100%' },
                              '& .MuiAlert-icon': { py: 0.5, alignItems: 'flex-start' },
                            }}
                          >
                            <Typography variant="caption" component="span" fontWeight={600} display="block">
                              {warning.code ?? 'Voucher warning'}
                            </Typography>
                            {warning.message ? (
                              <Typography variant="caption" color="text.secondary" component="span" display="block">
                                {warning.message}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary" component="span" display="block">
                                Review this voucher before relying on this rule.
                              </Typography>
                            )}
                          </Alert>
                        ))}
                      </Stack>
                    ) : null}
                    {ruleVoucher ? (
                      <VoucherSummarySection voucher={ruleVoucher} />
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2" component="span">
                          The rule response did not include an embedded voucher (this field is optional). Voucher
                          ID:{' '}
                        </Typography>
                        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {existingRule.voucherId}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Link
                            component={RouterLink}
                            to={`/marketing/vouchers/${existingRule.voucherId}`}
                            underline="hover"
                          >
                            Open voucher in admin
                          </Link>
                        </Box>
                      </Alert>
                    )}
                  </AccordionDetails>
                </Accordion>
              ) : null}
            </Stack>
          )}
          {!isCreate && ruleId && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Test and apply on order</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>
                {showInfo ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Run a dry-run evaluation for an order, then trigger manual voucher auto-assign. Apply stays disabled
                    until a test shows this rule matched the order. Changing the order ID clears the last test.
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <TextField
                    label="Order ID"
                    value={orderIdForTest}
                    onChange={(e) => setOrderIdForTest(e.target.value)}
                    sx={{ minWidth: 360 }}
                    helperText={
                      showInfo ? 'UUID of the purchase order to evaluate against this saved rule.' : undefined
                    }
                  />
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleTestRuleOnOrder}
                      disabled={dryRunMutation.isPending}
                    >
                      Test rule on order
                    </Button>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={handleManualApplyToOrder}
                      disabled={!canApplyRuleToTestedOrder || manualApplyMutation.isPending || !isPromoAdmin}
                    >
                      Apply to order
                    </Button>
                  </Stack>
                </Stack>
                {testResult && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Evaluation mode: {testResult.result.mode}
                    </Typography>
                    {!ruleOutcomeForOrder && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        No outcome entry for this rule was returned. Other rules may still appear in the full response.
                      </Alert>
                    )}
                    {ruleOutcomeForOrder && (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                          <Typography variant="body2">This rule:</Typography>
                          <Chip
                            size="small"
                            label={ruleOutcomeForOrder.matched ? 'Matched' : 'Did not match'}
                            color={ruleOutcomeForOrder.matched ? 'success' : 'default'}
                          />
                          {ruleOutcomeForOrder.skipReason ? (
                            <Typography variant="body2" color="text.secondary">
                              {ruleOutcomeForOrder.skipReason}
                            </Typography>
                          ) : null}
                        </Stack>
                        {ruleOutcomeForOrder.voucherUserId ? (
                          <Typography variant="body2" color="text.secondary">
                            Voucher user (dry-run): {ruleOutcomeForOrder.voucherUserId}
                          </Typography>
                        ) : null}
                        {ruleOutcomeForOrder.conditionOutcomes?.length ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              Conditions
                            </Typography>
                            <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={1}>
                              {ruleOutcomeForOrder.conditionOutcomes.map((c, idx) => {
                                const metadataText = formatConditionCheckMetadata(getConditionOutcomeMetadata(c));
                                return (
                                  <Box key={`${c.condition_type}-${idx}`} component="li" sx={{ display: 'list-item' }}>
                                    <Typography variant="body2" component="div">
                                      {c.condition_type}: {c.passed ? 'passed' : 'failed'}
                                      {c.error_codes?.length ? ` (${c.error_codes.join(', ')})` : ''}
                                    </Typography>
                                    {metadataText ? (
                                      <Box sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Condition check details
                                        </Typography>
                                        <Typography
                                          component="pre"
                                          variant="caption"
                                          sx={{
                                            m: 0,
                                            mt: 0.25,
                                            p: 1,
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                            fontFamily: 'monospace',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                          }}
                                        >
                                          {metadataText}
                                        </Typography>
                                      </Box>
                                    ) : null}
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>
                        ) : null}
                      </Stack>
                    )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}
          {!isCreate && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                mt: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Test and apply on multiple orders</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>
                {showInfo ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Search orders by filter, review whether auto-assign conditions pass, inspect failure reasons, and
                    apply assignment directly per order.
                  </Typography>
                ) : null}
                <VoucherOrderAutoAssignByOrderFiltersTable
                  onApplyOrder={handleManualApplyFromSearch}
                  applyingOrderId={bulkApplyOrderId}
                  canAssign={isPromoAdmin}
                />
              </AccordionDetails>
            </Accordion>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <PageSubheader title="Voucher user lookup" />
            {showInfo ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="div">
                Voucher user assigned by this rule
              </Typography>
            ) : null}
            {resolvedVoucherId && (
              <VoucherUsersSearchPage
                embedded
                defaultAccordionsOpen={false}
                voucherId={resolvedVoucherId}
                defaultVoucherUserReportingTag={VOUCHER_USER_REPORTING_TAG_AUTO_ASSIGN_ORDER}
              />
            )}
          </Paper>

          <Dialog open={deleteConfirmOpen} onClose={() => !deleteMutation.isPending && setDeleteConfirmOpen(false)}>
            <DialogTitle>Delete this voucher auto-assign rule?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This removes the rule permanently. Orders will no longer be evaluated against it. This cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                variant="text"
                color="primary"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => void handleConfirmDeleteRule()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>

          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <DebugModal type="generic" title="Page state (voucher auto-assign rule)" data={debugPageState} />
          </Box>
        </Box>
      </Container>
    </>
  );
}
